import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaMagic, FaChevronDown, FaChevronUp, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { goReserve, goBackSmart } from '../../utils/navigation';
import { useTranslation } from 'react-i18next';

interface FaqMedicalSkincareProps {
  ipAllowed: boolean;
}

type QA = { q: string; a: string | string[] };
type AnyObj = Record<string, any>;

async function loadFromPublic(lang: 'ja' | 'zh'): Promise<AnyObj | null> {
  try {
    const res = await fetch(`/data/faq_${lang}.json`, { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as AnyObj;
  } catch {
    return null;
  }
}

function pick<T>(root: any, path: string, fallback: T): T {
  if (!root) return fallback;
  const segs = path.split('.');
  let cur: any = root;
  for (const s of segs) {
    if (cur && typeof cur === 'object' && s in cur) cur = cur[s];
    else return fallback;
  }
  return cur as T;
}

const FaqMedicalSkincare: React.FC<FaqMedicalSkincareProps> = ({ ipAllowed }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang: 'ja' | 'zh' = i18n.language === 'zh' ? 'zh' : 'ja';

  const LOCAL = {
    ja: {
      page: {
        title: 'メディカルスキンケア FAQ',
        back: '戻る',
        cta_menu: 'メディカルスキンケアのメニューへ',
        cta_reserve: 'オンラインで予約する',
        disclaimer: '※ 本ページの内容は一般的な情報です。服用を検討される際は医師の診察を受けてください。',
      },
      overview: {
        title: 'メディカルスキンケアについて',
        paras: [
          'シミや肝斑、くすみ、ニキビなどの肌トラブルに対し、内服薬を用いて体の内側からケアする治療です。',
          '市販薬よりも有効成分の含有量が多い「医療用医薬品」を処方するため、より早く確実な効果が期待できます。',
          'トラネキサム酸、ユベラ、シナールなどを組み合わせて処方します。',
        ],
      },
      meds: {
        title: '主な処方薬と効果',
        items: [
          { k: 'トラネキサム酸', v: 'メラニン生成を抑制し、肝斑やシミ・そばかすを改善します。' },
          { k: 'ユベラ（ビタミンE）', v: '血行を促進し、肌のターンオーバーを正常化します。' },
          { k: 'シナール', v: 'ビタミンCとパントテン酸を配合。メラニン抑制とコラーゲン生成を助けます。' },
          { k: 'L-システイン', v: 'メラニンを無色化し、肌の新陳代謝を整えます。' },
          { k: '十味敗毒湯', v: '抗炎症作用のある漢方で、ニキビや肌の赤みを改善します。' },
        ],
      },
      qa: [
        { q: '市販のサプリとの違いは？', a: '有効成分の含有量が多い「医療用医薬品」であるため、高い効果が期待できます。' },
        { q: '効果が出るまでどのくらい？', a: 'ターンオーバーに合わせて1〜3ヶ月程度の継続をおすすめします。' },
      ],
    },
    zh: {
      page: {
        title: '医疗级护肤 FAQ',
        back: '返回',
        cta_menu: '前往医疗级护肤菜单',
        cta_reserve: '在线预约',
        disclaimer: '※ 本页面内容仅为基础信息。具体用药请遵医嘱。',
      },
      overview: {
        title: '关于医疗级护肤',
        paras: [
          '医疗护肤是通过口服药物，由内而外改善黄褐斑、色斑、暗沉、痤疮等肌肤问题的治疗方案。',
          '相比市售药品，处方药的有效成分含量更高，疗效更明确。',
          '我们将根据您的具体症状，组合处方氨甲环酸、维生素 E、希那尔等药物。',
        ],
      },
      meds: {
        title: '主要处方药及其作用',
        items: [
          { k: '氨甲环酸 (Tranexamic Acid)', v: '抑制黑色素生成，有效改善黄褐斑、色斑及雀斑。' },
          { k: '维生素 E (Juvela)', v: '促进血液循环与代谢，防止色斑沈淀。' },
          { k: '希那尔 (Cinal)', v: '减少黑色素并促进胶原蛋白合成，提升肌肤透明感。' },
          { k: 'L-半胱氨酸', v: '促进黑色素无色化，使肤色均匀。' },
          { k: '十味败毒汤', v: '对改善痤疮、粉刺及发红非常有效的汉方药。' },
        ],
      },
      qa: [
        { q: '和超市买的维生素有什么区别？', a: '处方药浓度更高，且由医生根据体质开具，更有针对性。' },
        { q: '多久能见效？', a: '结合皮肤代谢周期，建议连续服用 1〜3 个月。' },
      ],
    },
  } as const;

  const [root, setRoot] = useState<AnyObj | null>(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      const remote = await loadFromPublic(lang);
      if (!mounted) return;
      setRoot(remote);
    })();
    return () => { mounted = false; };
  }, [lang]);

  const J = pick<any>(root, 'faq.medical_skincare', null);

  const page = pick(J, 'page', LOCAL[lang].page);
  const OVERVIEW_DATA = pick(J, 'overview', LOCAL[lang].overview);
  const MEDS_DATA = pick(J, 'meds', LOCAL[lang].meds);
  const QA_LIST: QA[] = pick<any>(J, 'qa', LOCAL[lang].qa) as QA[];

  const [open, setOpen] = useState<number | null>(0);
  const toggle = (idx: number) => setOpen((cur) => (cur === idx ? null : idx));

  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: QA_LIST.slice(0, 25).map((qa) => ({
        '@type': 'Question',
        name: qa.q,
        acceptedAnswer: { '@type': 'Answer', text: Array.isArray(qa.a) ? qa.a.join('\n') : qa.a },
      })),
    }),
    [QA_LIST]
  );

  return (
    <div className="min-h-screen pt-28 pb-20 bg-emerald-50/70 font-sans" translate="no">
      <Helmet>
        <title>{page.title} - Melodis Clinic</title>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="container mx-auto px-4 max-w-5xl">
        <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-6 md:p-9">
          {/* Back */}
          <div className="mb-3">
            <button
              type="button"
              onClick={() => goBackSmart(navigate, '/faq')}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm"
            >
              <FaArrowLeft className="w-4 h-4" />
              {page.back}
            </button>
          </div>

          {/* Header */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-emerald-100 p-3">
              <FaMagic className="text-emerald-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
              <span className="text-emerald-400 mr-2">│</span>
              {page.title}
            </h1>
          </div>

          {/* Overview */}
          <section className="rounded-2xl border border-emerald-100 bg-emerald-50 p-6 md:p-7 mb-8">
            <h2 className="text-2xl font-semibold text-emerald-700 mb-3">{OVERVIEW_DATA.title}</h2>
            <div className="space-y-3 text-slate-800 leading-relaxed">
              {(OVERVIEW_DATA.paras || []).map((p: string, i: number) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </section>

          {/* Meds */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-4">{MEDS_DATA.title}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {(MEDS_DATA.items || []).map((card: any, idx: number) => (
                <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50 p-5 hover:bg-emerald-50/50 transition-colors">
                  <div className="font-semibold text-slate-900 mb-1">{card.k}</div>
                  <div className="text-slate-700 text-sm">{card.v}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Q&A */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">
              {lang === 'zh' ? '常见问题' : 'よくある質問'}
            </h3>
            <div className="space-y-3">
              {QA_LIST.map((qa, idx) => {
                const isOpen = open === idx;
                return (
                  <div key={idx} className="border border-slate-200 rounded-xl bg-slate-50 overflow-hidden">
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => toggle(idx)}
                      className="w-full flex items-start justify-between gap-4 px-4 py-3 text-left hover:bg-slate-100 transition-colors"
                    >
                      <span className="font-medium text-slate-900">{qa.q}</span>
                      {isOpen ? (
                        <FaChevronUp className="mt-1 shrink-0 text-slate-400" />
                      ) : (
                        <FaChevronDown className="mt-1 shrink-0 text-slate-400" />
                      )}
                    </button>
                    <div className={isOpen ? "px-4 pb-4 animate-in slide-in-from-top-2 duration-200" : "hidden"}>
                      <div className="text-slate-800 bg-white p-3 rounded-lg border border-slate-100 shadow-sm">
                        {Array.isArray(qa.a) ? (
                          <ul className="list-disc pl-5 space-y-1">
                            {(qa.a as string[]).map((line, i) => (
                              <li key={i}>{line}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>{qa.a as string}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-slate-500 text-xs mt-4">{page.disclaimer}</p>
          </section>

          {/* CTA */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => navigate('/menu/medical-skincare')}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-slate-700 hover:bg-slate-50 transition-all"
            >
              {page.cta_menu}
            </button>
            <button
              onClick={() => goReserve(navigate, ipAllowed)}
              className="inline-block bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-emerald-200 transition-all"
            >
              {page.cta_reserve}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqMedicalSkincare;
